import React, { useEffect } from "react";
import CustomModalV1 from "components/custom-modal/CustomModalV1";
import ButtonV1 from "components/button/Button";
import {
  XIconV1,
  ArrowDownIconV1,
  ArrowUpIconV1,
} from "images/image-component";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "store/store";
import {
  networkTypeSelector,
  cardNumberByAddressSelector,
} from "store/userSliceHelpers";
import { fetchCardNumber } from "store/cardNumberThunks";
import { txExplorerUrl } from "utils/network";
import formatDate, { formatTime } from "utils/DateUtil";
import "./transaction-detail-modal.css";
import { Payment } from "types/payment";
import { getPaymentKind, PAYMENT_KIND_LABEL } from "utils/payment";
import { useRedeemAddress } from "features/config/useAppConfig";
import CardNumber from "components/card-number/CardNumber";
import { Skeleton } from "components/skeleton";

interface Props {
  isOpen: boolean;
  handleClose: () => void;
  transaction: Payment;
  tokenSymbol: string;
}

function TransactionDetailModal({
  isOpen,
  handleClose,
  transaction,
  tokenSymbol,
}: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const networkType = useSelector(networkTypeSelector);

  const isIn = transaction?.dir === "in";

  // Sale (in) · Redeem (out → treasury) · Refund (out → customer). Decided from
  // the destination address — no card lookup needed, so it resolves instantly.
  const redeemAddress = useRedeemAddress();
  const kind = transaction
    ? getPaymentKind(transaction, redeemAddress)
    : "sale";
  const type = PAYMENT_KIND_LABEL[kind];

  // The customer on this payment: a sale credits us FROM them, a refund debits
  // us TO them. A redeem goes to the treasury (no card), so we resolve one only
  // for sales and refunds.
  const counterparty =
    kind === "sale"
      ? transaction?.from
      : kind === "refund"
        ? transaction?.to
        : undefined;

  const cardNumber = useSelector(
    cardNumberByAddressSelector(counterparty ?? ""),
  );

  useEffect(() => {
    if (!isOpen || !counterparty) return;
    dispatch(fetchCardNumber(counterparty));
  }, [isOpen, counterparty, dispatch]);

  const date = new Date(Number(transaction?.time) * 1000);

  // The From/To row shows the customer — present for sales and refunds, absent
  // for a redeem (the treasury has no card).
  const showFrom = kind !== "redeem";

  const txExplorer = txExplorerUrl(networkType, transaction?.hash);

  return (
    <CustomModalV1 isOpen={isOpen} handleClose={handleClose}>
      <button
        className="v1-modal-close"
        onClick={handleClose}
        aria-label="Close"
      >
        <XIconV1 />
      </button>

      <div className="v1-tx-detail">
        <div className="v1-tx-detail-header">
          <div className={`v1-tx-detail-dir-icon ${isIn ? "in" : "out"}`}>
            {isIn ? <ArrowDownIconV1 size={18} /> : <ArrowUpIconV1 size={18} />}
          </div>
          <div className="v1-tx-detail-heading">
            <h3 className="v1-tx-detail-title">{type}</h3>
            <span className="v1-tx-detail-date">
              {formatDate(date)} · {formatTime(date)}
            </span>
          </div>
        </div>

        <div className="v1-tx-detail-amount-row">
          <span className={`v1-tx-detail-amount-value ${isIn ? "in" : "out"}`}>
            {isIn ? "+" : "−"}
            {transaction?.value.toFixed(2)}
          </span>
          <span className="v1-tx-detail-amount-unit">{tokenSymbol}</span>
        </div>

        <div className="v1-tx-detail-rows">
          {showFrom && (
            <div className="v1-tx-detail-row">
              <span className="v1-tx-detail-row-label">
                {isIn ? "From" : "To"}
              </span>
              <span className="v1-tx-detail-row-value">
                {cardNumber ? (
                  <CardNumber value={cardNumber} />
                ) : (
                  <Skeleton width={110} height={16} />
                )}
              </span>
            </div>
          )}
          <div className="v1-tx-detail-row">
            <span className="v1-tx-detail-row-label">Transaction ID</span>
            <span className="v1-tx-detail-row-value">{transaction?.txId}</span>
          </div>
        </div>

        <div className="v1-tx-detail-status">{transaction?.status}</div>

        <ButtonV1
          onClick={() => {
            window.open(txExplorer, "_blank");
          }}
          className="v1-auth-submit"
        >
          View in Explorer
        </ButtonV1>
      </div>
    </CustomModalV1>
  );
}

export default TransactionDetailModal;
